import * as React from 'react';
import Moment from 'moment';
import propz from 'propz';
import { formatTeamAgesCompact } from 'Src/helpers/ageGroups';
import { TOURNAMENT_GENDER_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/tournament';

interface Props {
  events: any[];
  selectedEventIds: string[];
  error?: string;
  onChange: (eventIds: string[]) => void;
}

export function TournamentInviteEventSelection({ events, selectedEventIds, error, onChange }: Props) {
  const [sportSearch, setSportSearch] = React.useState('');
  if (!events.length) return null;

  const normalizedSearch = sportSearch.trim().toLowerCase();
  const visibleEvents = normalizedSearch
    ? events.filter(event => String(propz.get(event, ['sport', 'name'], '')).toLowerCase().includes(normalizedSearch))
    : events;
  const visibleEventIds = visibleEvents.map(event => String(event.id || event._id));
  const areAllVisibleSelected = visibleEventIds.length > 0 && visibleEventIds.every(id => selectedEventIds.includes(id));

  const setAllVisible = (checked: boolean) => {
    onChange(checked
      ? Array.from(new Set([...selectedEventIds, ...visibleEventIds]))
      : selectedEventIds.filter(id => !visibleEventIds.includes(id)));
  };

  const toggleEvent = (eventId: string) => {
    onChange(
      selectedEventIds.indexOf(eventId) === -1
        ? [...selectedEventIds, eventId]
        : selectedEventIds.filter(selectedEventId => selectedEventId !== eventId)
    );
  };

  return (
    <section className="tournamentBookingModal__eventSelection">
      <div className="tournamentBookingModal__eventSelectionHeading">
        <div>
          <h3>Choose events</h3>
          <p>Select the events your school wants to enter.</p>
        </div>
        <span>{selectedEventIds.length} of {events.length} selected</span>
      </div>
      <div className="tournamentBookingModal__eventTools">
        <input
          type="search"
          value={sportSearch}
          onChange={event => setSportSearch(event.target.value)}
          placeholder="Search by sport"
          aria-label="Search events by sport"
        />
        <label className="tournamentBookingModal__selectAll">
          <input
            type="checkbox"
            checked={areAllVisibleSelected}
            onChange={event => setAllVisible(event.target.checked)}
            disabled={!visibleEventIds.length}
          />
          <span aria-hidden="true" />
          Select all{normalizedSearch ? ' results' : ''}
        </label>
      </div>
      <div className="tournamentBookingModal__eventOptions">
        {!visibleEvents.length ? <p className="tournamentBookingModal__eventEmpty">No events found for this sport.</p> : null}
        {visibleEvents.map(event => {
          const eventId = String(event.id || event._id);
          const description = propz.get(event, ['eventDetails', 'shortDescription'], '') || event.shortDescription;
          const sportName = propz.get(event, ['sport', 'name'], '') || event.sportName || 'Tournament event';
          const ages = formatTeamAgesCompact(event.ages, 'ENGLISH');
          const gender = TOURNAMENT_GENDER_SERVER_TO_CLIENT_MAPPING[event.gender] || event.gender;
          const schoolName = [sportName, ages, gender].filter(Boolean).join(' · ');
          const startTime = event.startTime ? Moment(event.startTime).format('ddd D MMM, HH:mm') : '';
          const checked = selectedEventIds.indexOf(eventId) !== -1;
          return (
            <label key={eventId} className={`tournamentBookingModal__eventOption ${checked ? 'isSelected' : ''}`}>
              <input type="checkbox" checked={checked} onChange={() => toggleEvent(eventId)} />
              <span className="tournamentBookingModal__eventCheck" aria-hidden="true">{checked ? '✓' : ''}</span>
              <span><strong>{description || schoolName}</strong><small>{[schoolName, startTime].filter(Boolean).join(' · ')}</small></span>
            </label>
          );
        })}
      </div>
      {error && <p className="tournamentBookingModal__eventError" role="alert">{error}</p>}
    </section>
  );
}
